import { useState } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight } from "lucide-react";
import { BudgetDeviationBadge } from "@/components/reports/budget-deviation-badge/BudgetDeviationBadge";
import { BudgetRow } from "@/lib/analytics/types";
import { formatNumber } from "@/lib/currency.utils";

export interface BudgetCategoryGroup {
  categoryId: string;
  categoryName: string;
  budgeted: number;
  actual: number;
  deviation: number;
  subCategories: BudgetRow[];
}

interface BudgetCategoryRowProps {
  group: BudgetCategoryGroup;
  displayCurrency: string;
  convert: (amount: number, from: string, to: string) => number;
}

export function BudgetCategoryRow({ group, displayCurrency, convert }: BudgetCategoryRowProps) {
  const [expanded, setExpanded] = useState(false);

  // Parent row totals
  const formattedGroupBudgeted = formatNumber(group.budgeted);
  const formattedGroupActual = formatNumber(group.actual);
  const formattedGroupDeviation = group.deviation;

  return (
    <>
      <TableRow>
        <TableCell>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 mr-2"
            onClick={() => setExpanded((v) => !v)}
            aria-label={
              expanded
                ? `Collapse ${group.categoryName}`
                : `Expand ${group.categoryName}`
            }
            disabled={group.subCategories.length === 0}>
            {group.subCategories.length > 0 ? (
              expanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )
            ) : null}
          </Button>
          {group.categoryName}
        </TableCell>
        <TableCell>{displayCurrency}</TableCell>
        <TableCell className="text-right">{formattedGroupBudgeted}</TableCell>
        <TableCell className="text-right">{formattedGroupActual}</TableCell>
        <TableCell className="text-right">
          <BudgetDeviationBadge deviation={formattedGroupDeviation} />
        </TableCell>
      </TableRow>
      {expanded &&
        group.subCategories.map((sub) => {
          const formattedBudgetedValue = formatNumber(
            convert(sub.budgeted, sub.currency, displayCurrency),
          );
          const formattedActualValue = formatNumber(
            convert(sub.actual, sub.currency, displayCurrency),
          );
          const formattedDeviationValue = convert(
            sub.deviation,
            sub.currency,
            displayCurrency,
          );

          return (
            <TableRow key={sub.subCategoryId} className="bg-muted/30">
              <TableCell className="pl-10">{sub.subCategoryName}</TableCell>
              <TableCell className="pl-10">{displayCurrency}</TableCell>
              <TableCell className="text-right">{formattedBudgetedValue}</TableCell>
              <TableCell className="text-right">{formattedActualValue}</TableCell>
              <TableCell className="text-right">
                <BudgetDeviationBadge deviation={formattedDeviationValue} />
              </TableCell>
            </TableRow>
          );
        })}
    </>
  );
}
